import type { ReactNode } from 'react';

/**
 * DESIGN.md's module pastels, as literal class strings so Tailwind sees them.
 *
 * **A module's pastel appears on a section marker and on that module's tab,
 * nowhere else** - RESTAURANT phase 9 design brief, open decision 2. Never a
 * card background, never a button. A pastel that spreads stops being an
 * identity and becomes wallpaper (The Two Languages Rule).
 *
 * D-055: moved here from RESTAURANT when LIBRARY needed it. All six are now
 * listed because the tab bar (`shared/layout/TabBar.tsx`) gives each module's
 * active tab its own chip, which is the wayfinding job DESIGN.md §Tertiary
 * describes: "the one piece of colour a student learns without being taught".
 */
export const CHIP = {
  announcements: 'bg-module-announcements text-on-module-announcements',
  complaints: 'bg-module-complaints text-on-module-complaints',
  events: 'bg-module-events text-on-module-events',
  carpooling: 'bg-module-carpooling text-on-module-carpooling',
  restaurant: 'bg-module-restaurant text-on-module-restaurant',
  library: 'bg-module-library text-on-module-library',
} as const;

export type ModuleTone = keyof typeof CHIP;

/** A section marker with the module's icon chip. `module` is required: a default would paint the wrong module. */
export function ModuleSection({
  module,
  icon,
  title,
  action,
  children,
}: {
  module: ModuleTone;
  icon: string;
  title: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <span
          className={`w-10 h-10 shrink-0 rounded-xl ${CHIP[module]} flex items-center justify-center`}
        >
          <span className="material-symbols-outlined text-[20px]">{icon}</span>
        </span>
        <h2 className="flex-1 min-w-0 font-headline-lg text-headline-lg text-on-surface truncate">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * The module's card geometry, taken from the partners' screens so the three
 * modules look like one application: `rounded-3xl`, `shadow-card`, white, 16px
 * of padding on a phone and 24 from `md`.
 */
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`bg-surface-container-lowest rounded-3xl shadow-card border border-hairline p-4 md:p-6 ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * DESIGN.md: a list loads as skeleton cards, never a centred spinner. A spinner
 * says "something is happening somewhere"; a skeleton says "three cards are
 * coming and here is where they will be", and the page does not jump when they
 * land.
 */
export function CardSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-4" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="h-24 rounded-3xl bg-surface-container animate-pulse" />
      ))}
    </div>
  );
}
